import { getCobroReal } from "../cobro/getCobroFactura";
import { buildSaldoInicialEntry } from "./saldoInicial";

/**
 * Construye el ledger contable de un cliente (movimientos + saldo acumulado).
 *
 * Convención de signo del saldo acumulado:
 *   - saldo > 0  → el cliente nos debe (saldo por cobrar)
 *   - saldo < 0  → le debemos al cliente (anticipos o saldo a favor del cliente)
 *
 * Débitos (suben el saldo por cobrar):
 *   - Facturas SII emitidas al cliente. NC (tipo_dte=61) entra como CRÉDITO POSITIVO
 *     (bucket "nota_credito"), no como débito negativo. Ver regla 10.42.
 *
 * Créditos (bajan el saldo por cobrar):
 *   - Cobro de cada factura: lo determina `getCobroReal(factura.id, ctxCobro)` — la verdad ÚNICA
 *     de cobro (MBF / FK-directa / factoring / cuotas), atribuida al cliente de ESA factura por
 *     `factura.cliente_id` (no por movimientos_caja.entidad_id). Monto = monto_total de la factura.
 *     Esto resuelve correctamente los giros multi-anunciante (export): cada factura se acredita a
 *     su anunciante, no el giro entero a un solo cliente.
 *   - Cobros a cuenta NO contra factura (cobro_credito / cobro_a_cuenta / sin categoría): se
 *     preservan tal cual desde movimientos_caja. Los `cobro_factura` quedan EXCLUIDOS (su cobro lo
 *     determina getCobroReal, para no doble-contar).
 *
 * Saldo inicial opcional al 2026-01-01 entra como entry sintética (origen "INI").
 *
 * Filtrado por entidad: SIEMPRE por cliente_id numérico, nunca por nombre.
 *
 * @param {object} args
 * @param {number} args.cliId                     ID numérico del cliente.
 * @param {Array}  args.siiEmitidas               Facturas SII emitidas del país.
 * @param {Array}  args.movimientosCaja           Movimientos de caja del país desde FECHA_CORTE.
 * @param {object} [args.saldoInicial]            Saldo inicial de la entidad (o null).
 * @param {string} args.monedaPrincipal           Moneda local del país ("CLP" o "PEN").
 * @param {Function} args.toDisplayCurrency       (monto, monedaOrigen, fecha) → monto en moneda display.
 * @param {object} [args.ctxCobro]                Contexto de buildContextoCobro (verdad de cobro por factura).
 * @returns {Array} Entries ordenadas DESC por fecha, con debitoDisplay/creditoDisplay/saldo.
 */
export const construirLedgerCliente = ({
  cliId,
  siiEmitidas,
  movimientosCaja,
  saldoInicial,
  monedaPrincipal,
  toDisplayCurrency,
  ctxCobro,
}) => {
  if (!cliId) return [];

  const facturasCliente = siiEmitidas.filter((f) => f.cliente_id === cliId);

  const debitos = facturasCliente.map((f) => ({
    fecha: f.fecha_emision,
    concepto: f.tipo_dte === 61 ? "Nota de crédito" : "Factura",
    origen: "SII",
    referencia: f.folio ? String(f.folio) : "-",
    // NC (tipo_dte=61) entra como CRÉDITO POSITIVO con bucket "nota_credito" (mismo
    // criterio que ledgerProveedor): reduce el saldo por cobrar pero queda en el haber.
    // El saldo corrido es idéntico (0 - X == -X - 0). Ver regla 10.42.
    debito: f.tipo_dte === 61 ? 0 : Number(f.monto_total),
    credito: f.tipo_dte === 61 ? Number(f.monto_total) : 0,
    moneda: monedaPrincipal,
    bucket: f.tipo_dte === 61 ? "nota_credito" : "factura",
  }));

  // Crédito por factura: getCobroReal decide cobrada sí/no y la fecha del cobro. Monto = monto_total
  // (lo que el cliente pagó), atribuido a esta factura. Las NC (tipo_dte=61) no son cobros.
  const creditosFactura = [];
  if (ctxCobro) {
    for (const f of facturasCliente) {
      if (f.tipo_dte === 61) continue;
      const cobro = getCobroReal(f.id, ctxCobro);
      if (!cobro.cobrada) continue;
      creditosFactura.push({
        fecha: cobro.fecha || f.fecha_emision,
        concepto: "Cobro",
        origen: null,
        referencia: f.folio ? String(f.folio) : "-",
        descripcion: "",
        debito: 0,
        credito: Number(f.monto_total),
        moneda: monedaPrincipal,
        bucket: "factura",
      });
    }
  }

  // Cobros a cuenta NO contra factura: se preservan (los cobro_factura ya los cubre getCobroReal).
  const creditosACuenta = movimientosCaja
    .filter(
      (m) =>
        m.tipo === "cobro" &&
        m.entidad_tipo === "cliente" &&
        m.entidad_id === cliId &&
        m.categoria_pago !== "cobro_factura"
    )
    .map((m) => ({
      fecha: m.fecha,
      concepto: "Cobro",
      origen: null,
      referencia: m.numero_documento || "-",
      descripcion: m.descripcion || "",
      debito: 0,
      credito: Number(m.monto),
      moneda: m.moneda || monedaPrincipal,
      bucket: m.categoria_pago === "cobro_credito" || m.categoria_pago === "pago_a_cuenta" ? "credito" : "factura",
    }));

  const inicial = saldoInicial ? [{ ...buildSaldoInicialEntry(saldoInicial, monedaPrincipal), bucket: "credito" }] : [];
  const entries = [...inicial, ...debitos, ...creditosFactura, ...creditosACuenta].sort((a, b) =>
    a.fecha.localeCompare(b.fecha)
  );

  let saldo = 0;
  return entries
    .map((entry) => {
      const debitoDisplay = toDisplayCurrency(entry.debito, entry.moneda, entry.fecha);
      const creditoDisplay = toDisplayCurrency(entry.credito, entry.moneda, entry.fecha);
      saldo += debitoDisplay - creditoDisplay;
      return { ...entry, debitoDisplay, creditoDisplay, saldo };
    })
    .reverse();
};
